import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import SettingsGear from '../components/SettingsGear';
import OnboardingTour from '../components/OnboardingTour';
import axios from 'axios';
import { format } from 'date-fns';
import { Calendar } from '../components/ui/calendar';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import { Toaster, toast } from 'sonner';
import { LogOut, Star, Save, Trash2, Flame, Book, Pencil, ChevronLeft, Award, Plus, Send, Trophy } from 'lucide-react';

// Use relative URLs in production, full URL only in preview
const API_URL = window.location.hostname.includes('preview.emergentagent.com')
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

// Ensure cookies are sent with all requests
axios.defaults.withCredentials = true;

const MOODS = [
  { id: 'happy', label: 'Happy', img: '/kawaii/mood_happy.png' },
  { id: 'calm', label: 'Calm', img: '/kawaii/mood_calm.png' },
  { id: 'sad', label: 'Sad', img: '/kawaii/mood_sad.png' },
  { id: 'angry', label: 'Angry', img: '/kawaii/mood_angry.png' },
  { id: 'tired', label: 'Tired', img: '/kawaii/mood_tired.png' },
];

const STAR_COLORS = [
  { id: 'yellow', label: 'Yellow', hex: '#FDE047' },
  { id: 'pink', label: 'Pink', hex: '#F472B6' },
  { id: 'blue', label: 'Blue', hex: '#38BDF8' },
  { id: 'green', label: 'Green', hex: '#6EE7B7' },
  { id: 'purple', label: 'Purple', hex: '#C084FC' },
];

// ==================== EGG & BIRD ANIMATIONS ====================

// Bird types with fun facts
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_mood-diary-calendar/artifacts/sd96v0rx_image.png';

// Would You Rather — fetched daily from API
const DAILY_WYR_LIMIT = 30;

// Would You Rather Sliding Panel
const WouldYouRather = () => {
  const [open, setOpen] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [newOptA, setNewOptA] = useState('');
  const [newOptB, setNewOptB] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [dailyQuestions, setDailyQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [qIndex, setQIndex] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    const saved = localStorage.getItem('wyr_date');
    const savedIdx = parseInt(localStorage.getItem('wyr_index') || '0', 10);
    if (saved === today) return savedIdx;
    localStorage.setItem('wyr_date', today);
    localStorage.setItem('wyr_index', '0');
    localStorage.removeItem('wyr_picked');
    return 0;
  });
  const [picked, setPicked] = useState(() => localStorage.getItem('wyr_picked'));
  
  // Fetch questions when panel opens
  useEffect(() => {
    if (open && !fetched) {
      setLoading(true);
      axios.get(`${API_URL}/api/wyr/daily`)
        .then(res => {
          setDailyQuestions(res.data.questions || []);
          setFetched(true);
        })
        .catch(() => setDailyQuestions([]))
        .finally(() => setLoading(false));
    }
  }, [open, fetched]);
  
  const totalQ = dailyQuestions.length;
  const done = qIndex >= totalQ;
  
  const handlePick = (choice) => {
    setPicked(choice);
    localStorage.setItem('wyr_picked', choice);
  };
  
  const nextQuestion = () => {
    setPicked(null);
    localStorage.removeItem('wyr_picked');
    const next = qIndex + 1;
    setQIndex(next);
    localStorage.setItem('wyr_index', String(next));
  };

  const handleCreateQuestion = async () => {
    if (!newOptA.trim() || !newOptB.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/wyr/questions`, {
        option_a: newOptA.trim(),
        option_b: newOptB.trim(),
      });
      setNewOptA('');
      setNewOptB('');
      setShowCreate(false);
      // Re-fetch to include the new question
      const res = await axios.get(`${API_URL}/api/wyr/daily`);
      setDailyQuestions(res.data.questions || []);
      toast.success('Great! Our team will give out permission to approve your idea.', { duration: 5000 });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add question');
    } finally {
      setSubmitting(false);
    }
  };
  
  const q = done ? null : dailyQuestions[qIndex];
  
  return (
    <>
      {/* Toggle tab */}
      <button
        onClick={() => setOpen(!open)}
        className="fixed top-20 right-0 z-50 bg-[var(--c1)] border-[1.5px] border-r-0 border-[var(--border-soft)] rounded-l-full px-2 py-3 shadow-sm hover:px-3 transition-all"
        data-testid="wyr-toggle"
      >
        <ChevronLeft className={`w-5 h-5 text-[#1A1A1A] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Panel */}
      <div className={`fixed top-16 right-0 z-40 h-[calc(100vh-64px)] w-80 bg-white border-l-[1.5px] border-[var(--border-soft)] shadow-[-4px_0_20px_rgba(0,0,0,0.06)] transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="p-5 h-full flex flex-col">
          
          {/* Full-panel create mode */}
          {showCreate ? (
            <div className="h-full flex flex-col" data-testid="wyr-create-form">
              <h3 className="text-3xl font-heading font-bold text-[#1A1A1A] mb-1">Create a Question</h3>
              <p className="text-sm text-[#6B7280] font-medium mb-5">Everyone will see your question!</p>
              
              {/* Live preview — same card layout as the game */}
              <div className="flex-1 flex flex-col gap-4">
                <div className={`flex-1 rounded-3xl p-5 text-left transition-all border-[1.5px] ${
                  newOptA.trim() ? 'bg-[var(--c1-light)] border-[var(--c1)]' : 'bg-[#F9FAFB] border-dashed border-[#D4D4D8]'
                }`}>
                  <input
                    type="text"
                    placeholder="Type option A here..."
                    value={newOptA}
                    onChange={e => setNewOptA(e.target.value)}
                    maxLength={200}
                    className="w-full h-full bg-transparent text-lg font-bold text-[#1A1A1A] placeholder:text-[#C4C4C4] placeholder:font-medium focus:outline-none resize-none"
                    data-testid="wyr-create-option-a"
                  />
                </div>
                
                <div className="text-center font-heading text-2xl font-bold text-[#D4D4D8]">or</div>
                
                <div className={`flex-1 rounded-3xl p-5 text-left transition-all border-[1.5px] ${
                  newOptB.trim() ? 'bg-[var(--c3-light)] border-[var(--c3)]' : 'bg-[#F9FAFB] border-dashed border-[#D4D4D8]'
                }`}>
                  <input
                    type="text"
                    placeholder="Type option B here..."
                    value={newOptB}
                    onChange={e => setNewOptB(e.target.value)}
                    maxLength={200}
                    className="w-full h-full bg-transparent text-lg font-bold text-[#1A1A1A] placeholder:text-[#C4C4C4] placeholder:font-medium focus:outline-none resize-none"
                    data-testid="wyr-create-option-b"
                  />
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => { setShowCreate(false); setNewOptA(''); setNewOptB(''); }}
                  className="flex-1 py-3 rounded-full border-[1.5px] border-[var(--border-soft)] text-[#6B7280] font-semibold hover:bg-[#F9FAFB] transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateQuestion}
                  disabled={submitting || !newOptA.trim() || !newOptB.trim()}
                  className="flex-1 py-3 rounded-full bg-[var(--c1)] hover:bg-[var(--c1-accent)] text-[#1A1A1A] font-bold transition-all disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="wyr-create-submit"
                >
                  <Send className="w-4 h-4" /> {submitting ? 'Adding...' : 'Share'}
                </button>
              </div>
            </div>
          ) : (
            /* Normal game mode */
            <>
              <h3 className="text-3xl font-heading font-bold text-[#1A1A1A] mb-1">Would You Rather...</h3>
              
              {/* Create button — below title */}
              <button
                onClick={() => setShowCreate(true)}
                className="self-start mb-3 px-3 py-1 rounded-full bg-[var(--c2)] hover:bg-[var(--c2-hover)] transition-all border-[1.5px] border-[var(--border-soft)] shadow-sm flex items-center gap-1.5 text-xs font-semibold text-[#1A1A1A]"
                data-testid="wyr-create-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Create your own
              </button>
          
              <p className="text-sm text-[#6B7280] font-medium mb-5">
                {loading ? 'Loading fresh questions...' : done ? "You've answered all of today's questions!" : `Question ${qIndex + 1} of ${totalQ}`}
              </p>
          
              {loading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-8 h-8 border-3 border-[var(--c1)] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : done ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center gap-4">
                  <div className="text-6xl">&#127793;</div>
                  <p className="text-2xl font-heading font-bold text-[#1A1A1A]">All done for today!</p>
                  <p className="text-[#6B7280] font-medium">Come back tomorrow for fresh new questions</p>
                </div>
              ) : q ? (
                <>
                  <div className="flex-1 flex flex-col gap-4">
                    {q.created_by_name && q.type === 'user' && (
                      <p className="text-xs text-center text-[var(--c1-accent)] font-medium -mb-2">
                        ✨ Created by {q.created_by_name}
                      </p>
                    )}
                    <button
                      onClick={() => handlePick('a')}
                      className={`flex-1 rounded-3xl p-5 text-left font-bold text-lg transition-all border-[1.5px] ${
                        picked === 'a' 
                          ? 'bg-[var(--c1)] border-[var(--c1-accent)] shadow-md -translate-y-0.5' 
                          : picked === 'b'
                            ? 'bg-[#F9FAFB] border-[#E5E7EB] opacity-50'
                            : 'bg-[var(--c1-light)] border-[var(--border-soft)] hover:border-[var(--c1)] hover:shadow-sm'
                      }`}
                      data-testid="wyr-option-a"
                    >
                      <span className="text-[#1A1A1A]">{q.option_a}</span>
                    </button>
                    
                    <div className="text-center font-heading text-2xl font-bold text-[#D4D4D8]">or</div>
                    
                    <button
                      onClick={() => handlePick('b')}
                      className={`flex-1 rounded-3xl p-5 text-left font-bold text-lg transition-all border-[1.5px] ${
                        picked === 'b' 
                          ? 'bg-[var(--c3)] border-[var(--c3-hover)] shadow-md -translate-y-0.5' 
                          : picked === 'a'
                            ? 'bg-[#F9FAFB] border-[#E5E7EB] opacity-50'
                            : 'bg-[var(--c3-light)] border-[var(--border-soft)] hover:border-[var(--c3)] hover:shadow-sm'
                      }`}
                      data-testid="wyr-option-b"
                    >
                      <span className="text-[#1A1A1A]">{q.option_b}</span>
                    </button>
                  </div>
                  
                  {picked && (
                    <button
                      onClick={nextQuestion}
                      className="mt-4 w-full py-3 rounded-full soft-btn bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A]"
                      data-testid="wyr-next"
                    >
                      Next Question
                    </button>
                  )}
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </>
  );
};

const BIRDS = [
  {
    id: 'robin',
    name: 'Robin',
    fact: 'Robins can eat up to 14 feet of earthworms in a day!',
    component: () => <img src="/kawaii/robin.png" alt="Robin" className="w-full h-full object-contain" draggable={false} />
  },
  {
    id: 'bluebird',
    name: 'Bluebird',
    fact: 'Bluebirds can spot a caterpillar from 60 feet away!',
    component: () => <img src="/kawaii/bluebird.png" alt="Bluebird" className="w-full h-full object-contain" draggable={false} />
  },
  {
    id: 'owl',
    name: 'Owl',
    fact: 'Owls can rotate their heads 270 degrees - almost all the way around!',
    component: () => <img src="/kawaii/owl.png" alt="Owl" className="w-full h-full object-contain" draggable={false} />
  },
  {
    id: 'penguin',
    name: 'Penguin',
    fact: 'Penguins propose to their partners with a pebble!',
    component: () => <img src="/kawaii/penguin.png" alt="Penguin" className="w-full h-full object-contain" draggable={false} />
  },
  {
    id: 'flamingo',
    name: 'Flamingo',
    fact: 'Flamingos are born grey and turn pink from eating shrimp!',
    component: () => <img src="/kawaii/flamingo.png" alt="Flamingo" className="w-full h-full object-contain" draggable={false} />
  },
  {
    id: 'parrot',
    name: 'Parrot',
    fact: 'Parrots can live for over 80 years - longer than most humans used to!',
    component: () => <img src="/kawaii/parrot.png" alt="Parrot" className="w-full h-full object-contain" draggable={false} />
  },
  {
    id: 'hummingbird',
    name: 'Hummingbird',
    fact: 'Hummingbirds can fly backwards and their hearts beat 1,200 times per minute!',
    component: () => <img src="/kawaii/hummingbird.png" alt="Hummingbird" className="w-full h-full object-contain" draggable={false} />
  },
  {
    id: 'chick',
    name: 'Baby Chick',
    fact: 'Chicks start communicating with their mothers while still inside the egg!',
    component: () => <img src="/kawaii/chick.png" alt="Baby Chick" className="w-full h-full object-contain" draggable={false} />
  }
];

// Mood to color mapping - more vibrant/saturated colors
const MOOD_COLORS = {
  happy: '#FFD700',   // Bright Gold Yellow
  calm: '#00D68F',    // Bright Teal Green
  sad: '#4A90FF',     // Bright Blue
  angry: '#FF5A8A',   // Bright Pink/Red
  tired: '#A855F7',   // Bright Purple
};

// Get random bird
const getRandomBird = () => {
  return BIRDS[Math.floor(Math.random() * BIRDS.length)];
};

// Egg — kawaii art
const EggSVG = ({ cracking }) => (
  <img 
    src={cracking ? "/kawaii/egg_crack.png" : "/kawaii/egg.png"}
    alt={cracking ? "Egg hatching" : "Egg"}
    className={`w-full h-full object-contain ${cracking ? 'animate-pulse' : ''}`}
    draggable={false}
  />
);

// Mini bird for grassland - with walking + pecking animations
const MiniBird = ({ bird, color, posX, isLeaving, isMe, peckDelay }) => {
  return (
    <div 
      className={`absolute ${isLeaving ? 'opacity-0 -translate-y-20' : ''}`}
      style={{ 
        left: `${posX}%`, 
        bottom: '18px',
        transition: 'left 4s ease-in-out',
      }}
    >
      {isMe && (
        <div className="flex flex-col items-center -mt-6 mb-0.5">
          <span className="text-[10px] font-bold text-[#334155] bg-white/80 rounded-full px-1.5 leading-tight">You</span>
          <svg width="12" height="10" viewBox="0 0 12 10" className="mt-0.5">
            <polygon points="6,10 1,1 11,1" fill="#334155"/>
          </svg>
        </div>
      )}
      <div 
        className="w-16 h-16 drop-shadow-lg" 
        style={{ 
          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
          animation: `birdPeck 8s ease-in-out ${peckDelay}s infinite`,
        }}
      >
        {bird.component()}
      </div>
    </div>
  );
};

// Grassland component showing active users as birds
const Grassland = ({ activeUsers, leavingBird }) => {
  const birdsRef = useRef({});       // stable bird type assignments keyed by id
  const [birds, setBirds] = useState([]);
  const [positions, setPositions] = useState({}); // current x positions keyed by bird id
  
  // Build stable bird list from mood distribution - only reassign bird type when a bird is new
  useEffect(() => {
    const newBirds = [];
    const prevBirds = birdsRef.current;
    const nextBirds = {};
    const myMood = activeUsers.my_mood;
    let myBirdPlaced = false;
    
    Object.entries(activeUsers.mood_distribution || {}).forEach(([mood, count]) => {
      for (let i = 0; i < Math.min(count, 8); i++) {
        const id = `${mood}-${i}`;
        const isMe = !myBirdPlaced && mood === myMood && i === 0;
        if (isMe) myBirdPlaced = true;
        
        // Reuse existing bird type if this id existed before
        const birdType = prevBirds[id]?.bird || BIRDS[Math.floor(Math.random() * BIRDS.length)];
        const peckDelay = prevBirds[id]?.peckDelay ?? (2 + Math.random() * 6);
        
        nextBirds[id] = { bird: birdType, peckDelay };
        newBirds.push({
          id,
          bird: birdType,
          color: MOOD_COLORS[mood] || '#FFE066',
          mood,
          isMe,
          peckDelay,
        });
      }
    });
    
    birdsRef.current = nextBirds;
    setBirds(newBirds);
    
    // Set initial positions for any new birds
    setPositions(prev => {
      const next = { ...prev };
      newBirds.forEach(b => {
        if (next[b.id] === undefined) {
          next[b.id] = 5 + Math.random() * 80; // random start across 5%-85%
        }
      });
      // Clean up old birds
      Object.keys(next).forEach(k => {
        if (!newBirds.find(b => b.id === k)) delete next[k];
      });
      return next;
    });
  }, [activeUsers]);

  // Periodically move birds to new random positions across the full grass
  useEffect(() => {
    const moveInterval = setInterval(() => {
      setPositions(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(id => {
          // Move by a random amount, staying within 2%-88%
          const shift = (Math.random() - 0.5) * 30; // ±15%
          next[id] = Math.max(2, Math.min(88, next[id] + shift));
        });
        return next;
      });
    }, 5000); // every 5 seconds
    
    return () => clearInterval(moveInterval);
  }, []);

  if (activeUsers.total_active === 0) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 h-32 pointer-events-none z-30 overflow-hidden">
      {/* Soft gradient sky */}
      <div className="absolute bottom-0 left-0 right-0 h-32" style={{ background: 'linear-gradient(to top, #dcfce7 0%, #f0fdf4 40%, transparent 100%)' }}></div>
      
      {/* Rolling hills - organic shapes */}
      <svg className="absolute bottom-0 w-full h-24" viewBox="0 0 1440 96" preserveAspectRatio="none">
        <path d="M0 96 L0 48 C120 20 240 55 360 38 C480 20 520 50 640 35 C760 18 880 52 1000 40 C1120 28 1200 50 1320 35 L1440 42 L1440 96 Z" fill="#bbf7d0" opacity="0.7"/>
        <path d="M0 96 L0 58 C100 45 200 65 320 52 C440 40 560 62 680 50 C800 38 920 58 1040 48 C1160 38 1280 55 1440 48 L1440 96 Z" fill="#86efac"/>
        <path d="M0 96 L0 72 C80 65 200 78 340 68 C480 58 600 75 720 66 C840 58 960 72 1100 65 C1200 60 1340 72 1440 66 L1440 96 Z" fill="#4ade80"/>
        <path d="M0 96 L0 82 C150 76 300 86 500 80 C700 74 900 84 1100 78 C1250 74 1380 82 1440 80 L1440 96 Z" fill="#22c55e"/>
      </svg>
      
      {/* Scattered meadow flowers */}
      <img src="/kawaii/meadow.png" alt="" className="absolute bottom-2 left-[6%] w-12 h-12 object-contain opacity-70" />
      <img src="/kawaii/meadow.png" alt="" className="absolute bottom-3 left-[48%] w-10 h-10 object-contain opacity-60 -scale-x-100" />
      <img src="/kawaii/meadow.png" alt="" className="absolute bottom-1 right-[18%] w-11 h-11 object-contain opacity-65" />
      
      {/* Birds */}
      {birds.map((b) => (
        <MiniBird 
          key={b.id} 
          bird={b.bird} 
          color={b.color} 
          posX={positions[b.id] ?? 50}
          isLeaving={leavingBird === b.id}
          isMe={b.isMe}
          peckDelay={b.peckDelay}
        />
      ))}
      
      {/* Active users count */}
      <div className="absolute bottom-3 right-4 bg-white/90 backdrop-blur-sm border-[1.5px] border-[var(--border-soft)] rounded-full px-4 py-1.5 text-sm font-bold text-[#1A1A1A] shadow-sm">
        🐦 {activeUsers.total_active} friend{activeUsers.total_active !== 1 ? 's' : ''} writing today
      </div>
    </div>
  );
};

// Streak Badge Component with Random Bird Hatching Animation
const StreakBadge = ({ streak, showHatching, onHatchComplete, currentBird }) => {
  const [phase, setPhase] = useState('egg');
  const [bird, setBird] = useState(null);
  
  useEffect(() => {
    if (showHatching && streak > 0) {
      setBird(currentBird || getRandomBird());
      setPhase('egg');
      
      const crack = setTimeout(() => setPhase('cracking'), 1000);
      const hatch = setTimeout(() => setPhase('hatching'), 2500);
      const birdPhase = setTimeout(() => setPhase('bird'), 3500);
      
      return () => {
        clearTimeout(crack);
        clearTimeout(hatch);
        clearTimeout(birdPhase);
      };
    } else {
      setPhase('minimized');
      if (!bird) setBird(getRandomBird());
    }
  }, [showHatching, streak, onHatchComplete, currentBird]);

  const dismiss = () => {
    setPhase('minimized');
    onHatchComplete?.();
  };

  if (streak === 0) {
    return (
      <div className="flex items-center gap-2 bg-[var(--c3-40)] border-[1.5px] border-[var(--border-soft)] rounded-full px-4 py-2 shadow-sm">
        <div className="w-8 h-8 opacity-50">
          <EggSVG cracking={false} />
        </div>
        <span className="text-sm text-[#1A1A1A] font-bold font-heading">Start your streak!</span>
      </div>
    );
  }

  // Full screen hatching animation
  if (phase !== 'minimized' && showHatching && bird) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm cursor-pointer" onClick={dismiss} data-testid="hatching-overlay">
        <div className="text-center max-w-md px-4" onClick={(e) => e.stopPropagation()}>
          <div className={`w-48 h-48 mx-auto transition-all duration-500 ${
            phase === 'hatching' ? 'animate-bounce' : ''
          } ${phase === 'bird' ? 'animate-pulse' : ''}`}>
            {phase === 'egg' && <EggSVG cracking={false} />}
            {phase === 'cracking' && <EggSVG cracking={true} />}
            {(phase === 'hatching' || phase === 'bird') && (
              <div className="relative">
                {phase === 'hatching' && (
                  <div className="absolute inset-0 animate-ping opacity-30">
                    {bird.component()}
                  </div>
                )}
                {bird.component()}
              </div>
            )}
          </div>
          <div className={`mt-4 transition-all duration-500 ${phase === 'bird' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <p className="text-4xl font-heading font-bold text-[#1A1A1A]">{streak} Day Streak!</p>
            <p className="text-xl text-[#4A5568] mb-2 font-semibold">A {bird.name} hatched for you!</p>
            <p className="text-sm text-[#4A5568] bg-white/90 border-[1.5px] border-[var(--border-soft)] rounded-xl px-4 py-2 inline-block shadow-sm">
              {bird.fact}
            </p>
            {phase === 'bird' && (
              <p className="mt-4 text-xs text-[#6B7280] animate-pulse font-medium">Tap anywhere to continue</p>
            )}
          </div>
          {phase === 'bird' && (
            <button onClick={dismiss} className="mt-6 px-6 py-2 rounded-full bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] font-heading font-bold text-base shadow-sm transition-all" data-testid="hatching-dismiss-button">
              Got it!
            </button>
          )}
        </div>
      </div>
    );
  }

  // Minimized badge in header
  return (
    <div className="flex items-center gap-2 bg-[var(--c2-40)] border-[1.5px] border-[var(--border-soft)] rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-all cursor-pointer" data-testid="streak-badge">
      <div className="w-8 h-8">
        {bird ? bird.component() : <EggSVG cracking={false} />}
      </div>
      <div className="flex items-center gap-1">
        <Flame className="w-4 h-4 text-[#1A1A1A]" />
        <span className="font-heading font-bold text-[#1A1A1A] text-lg">{streak}</span>
      </div>
      <span className="text-xs text-[#4A5568] font-bold hidden sm:inline">day streak</span>
    </div>
  );
};

// ====== THEME ILLUSTRATIONS ======
// Photo-based themes use real images, SVG themes kept for Starry Night + Cloud Dreams

// Starry Night - cosmic SVG (KEPT)
const ThemeStarryNight = () => (
  <svg viewBox="0 0 200 100" className="w-full h-full">
    <defs>
      <linearGradient id="nightBg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#0F172A"/>
        <stop offset="50%" stopColor="#1E1B4B"/>
        <stop offset="100%" stopColor="#312E81"/>
      </linearGradient>
    </defs>
    <rect fill="url(#nightBg)" width="200" height="100"/>
    <ellipse cx="100" cy="50" rx="90" ry="20" fill="white" opacity="0.05"/>
    <circle cx="30" cy="20" r="2" fill="white"/>
    <circle cx="80" cy="35" r="2.5" fill="white"/>
    <circle cx="150" cy="25" r="2" fill="white"/>
    <circle cx="170" cy="60" r="2.5" fill="white"/>
    <circle cx="50" cy="50" r="1.5" fill="white" opacity="0.9"/>
    <circle cx="100" cy="15" r="1.5" fill="white" opacity="0.9"/>
    <circle cx="130" cy="70" r="1.5" fill="white" opacity="0.9"/>
    <circle cx="20" cy="75" r="1.5" fill="white" opacity="0.8"/>
    <circle cx="40" cy="80" r="1" fill="white" opacity="0.7"/>
    <circle cx="70" cy="65" r="1" fill="white" opacity="0.6"/>
    <circle cx="90" cy="45" r="1" fill="white" opacity="0.7"/>
    <circle cx="110" cy="80" r="1" fill="white" opacity="0.6"/>
    <circle cx="140" cy="40" r="1" fill="white" opacity="0.7"/>
    <circle cx="160" cy="85" r="1" fill="white" opacity="0.6"/>
    <circle cx="180" cy="30" r="1" fill="white" opacity="0.7"/>
    <line x1="175" y1="15" x2="155" y2="35" stroke="white" strokeWidth="1.5" opacity="0.8"/>
    <circle cx="175" cy="15" r="2" fill="white"/>
  </svg>
);

// Cloud Dreams - sleeping kawaii clouds SVG (KEPT)
const ThemeCloudDream = () => (
  <svg viewBox="0 0 200 100" className="w-full h-full">
    <defs>
      <linearGradient id="dreamBg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#E0E7FF"/>
        <stop offset="100%" stopColor="#EDE9FE"/>
      </linearGradient>
    </defs>
    <rect fill="url(#dreamBg)" width="200" height="100"/>
    <polygon points="35,15 37,20 42,20 38,24 39,29 35,26 31,29 32,24 28,20 33,20" fill="#FBBF24" opacity="0.5"/>
    <polygon points="160,25 161,28 164,28 162,30 163,33 160,31 157,33 158,30 156,28 159,28" fill="#FBBF24" opacity="0.4"/>
    <ellipse cx="100" cy="50" rx="45" ry="25" fill="white"/>
    <ellipse cx="70" cy="45" rx="25" ry="20" fill="white"/>
    <ellipse cx="130" cy="45" rx="25" ry="20" fill="white"/>
    <ellipse cx="85" cy="38" rx="20" ry="15" fill="white"/>
    <ellipse cx="115" cy="38" rx="20" ry="15" fill="white"/>
    <path d="M85 48 Q89 52 93 48" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
    <path d="M107 48 Q111 52 115 48" stroke="#6B7280" strokeWidth="1.5" fill="none"/>
    <ellipse cx="80" cy="54" rx="5" ry="3" fill="#F9A8D4" opacity="0.4"/>
    <ellipse cx="120" cy="54" rx="5" ry="3" fill="#F9A8D4" opacity="0.4"/>
    <path d="M96 55 Q100 58 104 55" stroke="#9CA3AF" strokeWidth="1" fill="none"/>
    <text x="135" y="30" fill="#A5B4FC" fontSize="10" fontWeight="bold" opacity="0.6">z</text>
    <text x="143" y="22" fill="#A5B4FC" fontSize="8" fontWeight="bold" opacity="0.5">z</text>
    <ellipse cx="25" cy="75" rx="20" ry="10" fill="white" opacity="0.6"/>
    <ellipse cx="175" cy="80" rx="18" ry="9" fill="white" opacity="0.5"/>
    <circle cx="170" cy="18" r="10" fill="#FEF3C7" opacity="0.7"/>
    <circle cx="173" cy="16" r="8" fill="#EDE9FE"/>
  </svg>
);

// Photo theme - renders an image with full visibility
const PhotoTheme = ({ src, alt }) => (
  <div className="w-full h-full bg-[#F1F0FB] flex items-center justify-center">
    <img src={src} alt={alt} className="w-full h-full object-cover" loading="lazy" />
  </div>
);

// Theme image URLs
const THEME_PHOTOS = {
  'theme-ocean': 'https://images.pexels.com/photos/33987901/pexels-photo-33987901.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'theme-sakura': 'https://images.pexels.com/photos/31930564/pexels-photo-31930564.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'theme-tulips': 'https://images.unsplash.com/photo-1611010975967-ba9f6884a585?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxwaW5rJTIwdHVsaXBzJTIwZmxvd2VycyUyMHdhdGVyJTIwZHJlYW15JTIwYWVzdGhldGljfGVufDB8fHx8MTc3NTEyMDg1M3ww&ixlib=rb-4.1.0&q=85',
  'theme-lavender': 'https://images.pexels.com/photos/27578168/pexels-photo-27578168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'theme-cozy': 'https://images.pexels.com/photos/36260433/pexels-photo-36260433.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'theme-petals': 'https://images.unsplash.com/photo-1758399840247-117ee86c63dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHx3aGl0ZSUyMGZsb3dlcnMlMjBibHVlJTIwYWVzdGhldGljJTIwc29mdCUyMGRyZWFteSUyMGZsb3JhbHxlbnwwfHx8fDE3NzUxMjA4NzF8MA&ixlib=rb-4.1.0&q=85',
};

// Theme component renderer
const ThemeIllustration = ({ themeId }) => {
  if (themeId === 'theme-starry') return <ThemeStarryNight />;
  if (themeId === 'theme-cloud') return <ThemeCloudDream />;
  if (THEME_PHOTOS[themeId]) return <PhotoTheme src={THEME_PHOTOS[themeId]} alt={themeId} />;
  return <ThemeCloudDream />;
};

// Logo component — large, no container
const WillowLogo = ({ size = "h-10" }) => (
  <img src={LOGO_URL} alt="Willow Notes" className={`${size} object-contain`} />
);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { darkMode } = useTheme();
  const { t, getPrompt } = useLanguage();
  const navigate = useNavigate();
  
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [selectedMood, setSelectedMood] = useState(null);
  const [diaryContent, setDiaryContent] = useState('');
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [importantDates, setImportantDates] = useState([]);
  const [saving, setSaving] = useState(false);
  
  const [importantDateDialog, setImportantDateDialog] = useState(false);
  const [newImportantDate, setNewImportantDate] = useState({ title: '', star_color: '#FDE047' });
  
  // Streak state
  const [streak, setStreak] = useState(0);
  const [showHatching, setShowHatching] = useState(false);
  const [hasShownHatching, setHasShownHatching] = useState(false);
  const [currentBird, setCurrentBird] = useState(null);
  
  // Active users state
  const [activeUsers, setActiveUsers] = useState({ total_active: 0, mood_distribution: {} });
  const [leavingBird, setLeavingBird] = useState(null);
  
  // Sticker celebration state
  const [stickerCelebration, setStickerCelebration] = useState(null);
  const [stickerDefs, setStickerDefs] = useState({});

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);

  const fetchStreak = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/streak`, { withCredentials: true });
      const newStreak = response.data.streak;
      setStreak(newStreak);
      return newStreak;
    } catch (error) {
      console.error('Failed to fetch streak:', error);
      return 0;
    }
  }, []);

  const fetchActiveUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/active`, { withCredentials: true });
      setActiveUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch active users:', error);
    }
  }, []);

  const joinActiveUsers = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/users/active`, {}, { withCredentials: true });
      fetchActiveUsers();
    } catch (error) {
      console.error('Failed to join active users:', error);
    }
  }, [fetchActiveUsers]);

  const updateActiveUserMood = useCallback(async (mood) => {
    try {
      await axios.put(`${API_URL}/api/users/active`, { mood }, { withCredentials: true });
      fetchActiveUsers();
    } catch (error) {
      console.error('Failed to update mood:', error);
    }
  }, [fetchActiveUsers]);

  const checkStickers = useCallback(async () => {
    try {
      const res = await axios.post(`${API_URL}/api/stickers/check`, {}, { withCredentials: true });
      if (res.data.newly_earned?.length > 0) {
        // Fetch definitions if we don't have them yet
        if (Object.keys(stickerDefs).length === 0) {
          const defsRes = await axios.get(`${API_URL}/api/stickers`, { withCredentials: true });
          setStickerDefs(defsRes.data.definitions);
        }
        setStickerCelebration(res.data.newly_earned[0]);
        setTimeout(() => setStickerCelebration(null), 3000);
      }
    } catch (err) {
      // Silently fail sticker checks
    }
  }, [stickerDefs]);

  useEffect(() => {
    fetchThemes();
    fetchDiaryEntries();
    fetchImportantDates();
    joinActiveUsers();
    checkStickers();
    
    // Fetch streak and show hatching animation only once per day
    fetchStreak().then((s) => {
      const today = new Date().toISOString().split('T')[0];
      const lastHatchDate = localStorage.getItem('willow_last_hatch_date');
      if (s > 0 && lastHatchDate !== today) {
        setCurrentBird(getRandomBird());
        setShowHatching(true);
        setHasShownHatching(true);
        localStorage.setItem('willow_last_hatch_date', today);
      }
    });
    
    // Poll for active users every 10 seconds
    const interval = setInterval(fetchActiveUsers, 10000);
    
    return () => {
      clearInterval(interval);
    };
  }, []);

  // Show onboarding for first-time users (after hatching finishes, or immediately if no hatching)
  useEffect(() => {
    const hasOnboarded = localStorage.getItem('willow_onboarded');
    if (!hasOnboarded && !showHatching) {
      const timer = setTimeout(() => setShowOnboarding(true), 800);
      return () => clearTimeout(timer);
    }
  }, [showHatching]);

  const completeOnboarding = () => {
    setShowOnboarding(false);
    localStorage.setItem('willow_onboarded', 'true');
  };

  useEffect(() => {
    loadEntryForDate(selectedDate);
  }, [selectedDate, diaryEntries]);

  const fetchThemes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/themes`);
      setThemes(response.data.themes);
      if (response.data.themes.length > 0 && !selectedTheme) {
        setSelectedTheme(response.data.themes[0]);
      }
    } catch (error) {
      console.error('Failed to fetch themes:', error);
    }
  };

  const fetchDiaryEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/diary`, { withCredentials: true });
      setDiaryEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch diary entries:', error);
    }
  };

  const fetchImportantDates = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/important-dates`, { withCredentials: true });
      setImportantDates(response.data);
    } catch (error) {
      console.error('Failed to fetch important dates:', error);
    }
  };

  const loadEntryForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const entry = diaryEntries.find(e => e.date === dateStr);
    
    if (entry) {
      setDiaryContent(entry.content);
      setSelectedMood(entry.mood);
      const theme = themes.find(t => t.id === entry.theme_id);
      if (theme) setSelectedTheme(theme);
    } else {
      setDiaryContent('');
      setSelectedMood(null);
      if (themes.length > 0) setSelectedTheme(themes[0]);
    }
  };

  const saveDiaryEntry = async () => {
    if (!selectedTheme) {
      toast.error('Please select a theme first!');
      return;
    }

    // Always save for today's date only
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    
    setSaving(true);
    const isNewEntry = !diaryEntries.find(e => e.date === todayStr);

    try {
      // First try to update (PUT) - if entry exists it will succeed
      try {
        await axios.put(
          `${API_URL}/api/diary/${todayStr}`,
          { content: diaryContent, theme_id: selectedTheme.id, mood: selectedMood },
          { withCredentials: true }
        );
        toast.success('Entry updated!');
      } catch (putError) {
        // If PUT returns 404 (not found), create new entry with POST
        if (putError.response?.status === 404) {
          await axios.post(
            `${API_URL}/api/diary`,
            { date: todayStr, content: diaryContent, theme_id: selectedTheme.id, mood: selectedMood },
            { withCredentials: true }
          );
          toast.success('Entry saved!');
        } else {
          throw putError;
        }
      }
      fetchDiaryEntries();
      
      // Update mood in active users
      if (selectedMood) {
        updateActiveUserMood(selectedMood);
      }
      
      // Refresh streak and show hatching if it's a new entry
      const newStreak = await fetchStreak();
      if (isNewEntry && newStreak > 0) {
        setCurrentBird(getRandomBird());
        setShowHatching(true);
      }
      
      // Check for newly earned stickers
      checkStickers();
    } catch (error) {
      toast.error('Failed to save entry');
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };
  const addImportantDate = async () => {
    if (!newImportantDate.title.trim()) {
      toast.error('Please enter a title!');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/important-dates`,
        { date: format(selectedDate, 'yyyy-MM-dd'), title: newImportantDate.title, star_color: newImportantDate.star_color },
        { withCredentials: true }
      );
      toast.success('Important date added! ⭐');
      setImportantDateDialog(false);
      setNewImportantDate({ title: '', star_color: '#FDE047' });
      fetchImportantDates();
    } catch (error) {
      toast.error('Failed to add important date');
    }
  };

  const deleteImportantDate = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/important-dates/${id}`, { withCredentials: true });
      toast.success('Important date removed!');
      fetchImportantDates();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleLogout = async () => {
    // Leave active users and trigger bird animation
    try {
      await axios.delete(`${API_URL}/api/users/active`, { withCredentials: true });
    } catch (error) {
      console.error('Failed to leave active users:', error);
    }
    await logout();
    navigate('/login');
  };

  // Handle mood selection - update active users
  const handleMoodSelect = (moodId) => {
    setSelectedMood(moodId);
    updateActiveUserMood(moodId);
  };

  const getImportantDateForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return importantDates.find(d => d.date === dateStr);
  };

  const hasEntryForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return diaryEntries.some(e => e.date === dateStr);
  };

  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  return (
    <div className="min-h-screen bg-[var(--page-bg)]">
      <Toaster position="top-center" richColors />
      
      {/* Settings gear */}
      <SettingsGear />
      
      {/* Would You Rather sliding game panel */}
      <WouldYouRather />
      
      {/* Sticker Celebration Overlay */}
      {stickerCelebration && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/20 backdrop-blur-sm" data-testid="sticker-celebration-dashboard">
          <div className="flex flex-col items-center gap-4">
            <div className="w-32 h-32 animate-spin-slow">
              {/* Inline simple star for celebration since we don't have the full sticker SVG map here */}
              <svg viewBox="0 0 80 80" className="w-full h-full">
                <polygon points="40,8 48,30 72,30 52,44 60,68 40,54 20,68 28,44 8,30 32,30" fill="#FFD700" stroke="#FFA500" strokeWidth="1" />
              </svg>
            </div>
            <p className="text-2xl font-heading font-bold text-[#1A1A1A] bg-white/90 px-6 py-3 rounded-full shadow-lg">
              New sticker earned!
            </p>
            <p className="text-lg font-medium text-white bg-[#1A1A1A]/60 px-4 py-1 rounded-full">
              {stickerDefs[stickerCelebration]?.name || 'Check your awards!'}
            </p>
          </div>
        </div>
      )}
      
      {/* Hatching Animation Overlay (only shows during hatching) */}
      {showHatching && (
        <StreakBadge 
          streak={streak} 
          showHatching={showHatching} 
          onHatchComplete={() => setShowHatching(false)}
          currentBird={currentBird}
        />
      )}

      {/* Onboarding Tour (first-time users) */}
      {showOnboarding && <OnboardingTour onComplete={completeOnboarding} />}
      
      {/* Grassland with active users as birds */}
      <Grassland activeUsers={activeUsers} leavingBird={leavingBird} />
      
      {/* Navbar */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b-[1.5px] border-[var(--border-soft)] px-4 sm:px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Navigation Tabs */}
          <div className="flex items-center gap-2">
            <button
              className="px-5 py-2 rounded-full bg-[var(--c1)] border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A]"
              data-testid="nav-diary"
            >
              <Book className="w-4 h-4 inline mr-1" /> {t('diary')}
            </button>
            <button
              onClick={() => navigate('/drawings')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c3)] transition-colors"
              data-testid="nav-drawings"
            >
              <Pencil className="w-4 h-4 inline mr-1" /> {t('drawings')}
            </button>
            <button
              onClick={() => navigate('/stickers')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c2)] transition-colors"
              data-testid="nav-stickers"
            >
              <Award className="w-4 h-4 inline mr-1" /> {t('awards')}
            </button>
            <button
              onClick={() => navigate('/leaderboard')}
              className="px-5 py-2 rounded-full bg-white border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-lg text-[#1A1A1A] hover:bg-[var(--c2)] transition-colors"
              data-testid="nav-leaderboard"
            >
              <Trophy className="w-4 h-4 inline mr-1" /> Leaderboard
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Streak Badge */}
            <StreakBadge streak={streak} showHatching={false} currentBird={currentBird} />
            {user?.avatar && (
              <img src={`/avatars/${user.avatar}.png`} alt="avatar" className="w-8 h-8 rounded-full border-[1.5px] border-[var(--border-soft)] object-cover" data-testid="navbar-avatar" />
            )}
            <span className="text-[#1A1A1A] font-semibold hidden sm:inline">{t('hi')}, {user?.name}!</span>
            <button
              onClick={handleLogout}
              className="px-4 py-2 rounded-full border-[1.5px] border-[var(--border-soft)] shadow-sm bg-white hover:bg-[var(--c1)] text-[#1A1A1A] font-heading font-bold text-base transition-all"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4 inline mr-1" /> {t('logout')}
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Date Header with Logo */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <WillowLogo size="h-48" />
          <div className="text-center">
            <h2 className="text-5xl sm:text-6xl font-heading font-bold text-[#1A1A1A] mb-1">
              {format(selectedDate, 'EEEE')}
            </h2>
            <p className="text-lg text-[#4A5568] font-semibold">{format(selectedDate, 'MMMM d, yyyy')}</p>
          </div>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Calendar Card */}
          <div className="soft-card p-6" data-testid="calendar-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-heading font-bold text-[#1A1A1A]">{t('calendar')}</h3>
              <Dialog open={importantDateDialog} onOpenChange={setImportantDateDialog}>
                <DialogTrigger asChild>
                  <button className="px-4 py-2 rounded-full bg-[var(--c2)] border-[1.5px] border-[var(--border-soft)] shadow-sm font-heading font-bold text-base text-[#1A1A1A] hover:bg-[var(--c2-hover)] transition-all" data-testid="add-important-date-button">
                    <Star className="w-4 h-4 inline mr-1 fill-current" /> {t('markDay')}
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl border-[1.5px] border-[var(--border-soft)] shadow-lg" aria-describedby="important-date-description">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-3xl font-bold">{t('markImportantDay')}</DialogTitle>
                    <p id="important-date-description" className="text-sm text-[#4A5568]">{t('markImportantDayDesc')}</p>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label className="text-[#1A1A1A] font-semibold">{t('whatsSpecial')}</Label>
                      <Input
                        value={newImportantDate.title}
                        onChange={(e) => setNewImportantDate({ ...newImportantDate, title: e.target.value })}
                        placeholder="e.g., Birthday, Test, Trip!"
                        className="mt-2 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] focus:shadow-none"
                        data-testid="important-date-title-input"
                      />
                    </div>
                    <div>
                      <Label className="text-[#1A1A1A] font-semibold">{t('pickStarColour')}</Label>
                      <div className="flex gap-2 mt-2">
                        {STAR_COLORS.map((color) => (
                          <button
                            key={color.id}
                            onClick={() => setNewImportantDate({ ...newImportantDate, star_color: color.hex })}
                            className={`w-10 h-10 rounded-full border-[1.5px] border-[var(--border-soft)] transition-all ${newImportantDate.star_color === color.hex ? 'ring-2 ring-[var(--c3-hover)] ring-offset-2 scale-110' : 'shadow-sm'}`}
                            style={{ backgroundColor: color.hex }}
                            data-testid={`star-color-${color.id}`}
                          />
                        ))}
                      </div>
                    </div>
                    <button onClick={addImportantDate} className="w-full soft-btn bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] rounded-full py-3" data-testid="save-important-date-button">
                      <Star className="w-4 h-4 inline mr-2 fill-current" /> {t('saveImportantDay')}
                    </button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              month={calendarMonth}
              onMonthChange={setCalendarMonth}
              className="rounded-xl w-full"
              classNames={{
                months: "flex flex-col w-full",
                month: "space-y-4 w-full",
                caption: "flex justify-center pt-1 relative items-center",
                caption_label: "text-sm font-medium",
                nav: "space-x-1 flex items-center",
                head_row: "flex w-full",
                head_cell: "text-muted-foreground rounded-md flex-1 font-normal text-[0.8rem] text-center",
                row: "flex w-full mt-2",
                cell: "relative flex-1 p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected])]:rounded-md",
              }}
              modifiers={{
                hasEntry: (date) => hasEntryForDay(date),
                important: (date) => !!getImportantDateForDay(date),
              }}
              modifiersStyles={{
                hasEntry: { backgroundColor: 'var(--c3)', borderRadius: '50%' },
              }}
              components={{
                DayContent: ({ date }) => {
                  const importantDate = getImportantDateForDay(date);
                  return (
                    <div className="relative flex items-center justify-center w-full h-full">
                      <span>{date.getDate()}</span>
                      {importantDate && (
                        <Star
                          className="absolute -bottom-1 w-3 h-3 fill-current"
                          style={{ color: importantDate.star_color }}
                        />
                      )}
                    </div>
                  );
                },
              }}
              data-testid="calendar"
            />

            {importantDates.length > 0 && (
              <div className="mt-4 pt-4 border-t-[1.5px] border-[var(--border-soft)]">
                <h4 className="text-lg font-heading font-bold text-[#1A1A1A] mb-2">{t('upcoming')}</h4>
                <ScrollArea className="h-24">
                  {importantDates
                    .filter(d => new Date(d.date) >= new Date(format(new Date(), 'yyyy-MM-dd')))
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(0, 5)
                    .map((d) => (
                      <div key={d.id} className="flex items-center justify-between py-1.5 group">
                        <div className="flex items-center gap-2">
                          <Star className="w-4 h-4 fill-current" style={{ color: d.star_color }} />
                          <span className="text-sm font-semibold text-[#1A1A1A]">{d.title}</span>
                          <span className="text-xs text-[#4A5568] font-medium">{format(new Date(d.date), 'MMM d')}</span>
                        </div>
                        <button
                          onClick={() => deleteImportantDate(d.id)}
                          className="opacity-0 group-hover:opacity-100 text-[#4A5568] hover:text-[#1A1A1A] transition-opacity"
                          data-testid={`delete-important-date-${d.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Diary Entry Card — soft style */}
          <div className="lg:col-span-2 soft-card p-6 relative overflow-hidden" data-testid="diary-card">

            {/* Past/Future Date Notice */}
            {!isToday && (
              <div className="mb-4 px-4 py-3 rounded-full bg-[var(--c3-40)] border-[1.5px] border-[var(--border-soft)] text-center" data-testid="past-date-notice">
                <span className="text-sm font-semibold text-[#4A5568]">
                  {diaryContent ? 'Viewing past entry (read-only)' : 'No entry for this date'} &mdash;{' '}
                  <button onClick={() => setSelectedDate(new Date())} className="text-[#1A1A1A] underline decoration-[var(--c2)] decoration-2 hover:decoration-[var(--c1)] transition-colors font-bold">
                    Go to today
                  </button>
                </span>
              </div>
            )}

            {/* Theme Display */}
            {selectedTheme && (
              <div className="h-44 rounded-xl mb-6 overflow-hidden relative border-[1.5px] border-[var(--border-soft)] shadow-sm">
                <ThemeIllustration themeId={selectedTheme.id} />
                <div className="absolute bottom-3 left-4 bg-white/90 backdrop-blur-sm border-[1.5px] border-[var(--border-soft)] px-3 py-1 rounded-full shadow-sm">
                  <span className="font-heading font-bold text-[#1A1A1A] text-base">{selectedTheme.name}</span>
                </div>
              </div>
            )}

            {/* Theme Selector */}
            <div className={`mb-6 ${!isToday ? 'opacity-40 pointer-events-none' : ''}`}>
              <Label className="text-xl font-heading font-bold text-[#1A1A1A] mb-3 block">Pick Today's Theme</Label>
              <div className="flex gap-3 overflow-x-auto pb-2" data-testid="theme-selector">
                {themes.map((theme) => (
                  <button
                    key={theme.id}
                    onClick={() => setSelectedTheme(theme)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden theme-swatch ${
                      selectedTheme?.id === theme.id ? 'selected' : ''
                    }`}
                    title={theme.name}
                    data-testid={theme.id}
                  >
                    <ThemeIllustration themeId={theme.id} />
                  </button>
                ))}
              </div>
            </div>

            {/* Mood Tracker */}
            <div className={`mb-6 ${!isToday ? 'opacity-40 pointer-events-none' : ''}`}>
              <Label className="text-xl font-heading font-bold text-[#1A1A1A] mb-3 block">{t('howFeeling')}</Label>
              <div className="flex flex-wrap gap-3" data-testid="mood-tracker">
                {MOODS.map((mood) => (
                  <button
                    key={mood.id}
                    onClick={() => handleMoodSelect(mood.id)}
                    className={`mood-btn flex items-center gap-2 px-4 py-3 rounded-full font-bold transition-all ${
                      mood.id === 'happy' ? 'bg-[var(--c1-60)]' :
                      mood.id === 'calm' ? 'bg-[var(--c2-60)]' :
                      mood.id === 'sad' ? 'bg-[var(--c3-60)]' :
                      mood.id === 'angry' ? 'bg-[var(--c1)]' :
                      'bg-[var(--c3)]'
                    } ${
                      selectedMood === mood.id ? 'selected' : ''
                    }`}
                    data-testid={`mood-${mood.id}`}
                  >
                    <img src={mood.img} alt={mood.label} className="w-8 h-8 object-contain" draggable={false} />
                    <span className="hidden sm:inline text-[#1A1A1A]">{mood.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Diary Text Area */}
            <div className="mb-6">
              <Label className="text-xl font-heading font-bold text-[#1A1A1A] mb-3 block">
                {isToday ? 'Write about your day...' : `Entry for ${format(selectedDate, 'MMMM d, yyyy')}`}
              </Label>
              <textarea
                value={diaryContent}
                onChange={(e) => setDiaryContent(e.target.value)}
                placeholder={isToday ? getPrompt() : 'No entry written for this day'}
                readOnly={!isToday}
                className={`w-full h-48 p-6 resize-none text-[#1A1A1A] placeholder:text-[#C4B5D0] placeholder:italic diary-textarea outline-none font-medium ${!isToday ? 'opacity-60 cursor-not-allowed' : ''}`}
                data-testid="diary-textarea"
              />
            </div>

            {/* Save Button — only for today */}
            {isToday ? (
              <button
                onClick={saveDiaryEntry}
                disabled={saving}
                className="w-full h-14 soft-btn bg-[var(--c2)] hover:bg-[var(--c2-hover)] text-[#1A1A1A] disabled:opacity-50"
                data-testid="save-diary-button"
              >
                {saving ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Save className="w-5 h-5" /> {t('saveMyDay')}
                  </span>
                )}
              </button>
            ) : (
              <button
                onClick={() => setSelectedDate(new Date())}
                className="w-full h-14 soft-btn bg-[var(--c3)] hover:bg-[var(--c3-hover)] text-[#1A1A1A]"
                data-testid="go-to-today-button"
              >
                <span className="flex items-center justify-center gap-2">
                  Back to Today
                </span>
              </button>
            )}
          </div>
        </div>
        
        {/* Spacer for grassland */}
        <div className="h-28"></div>
      </main>
    </div>
  );
}
